/**
 * Dark Theme Token Values
 *
 * Extracted from the existing REDESIGN_COLORS, SEMANTIC_COLORS, and COMPONENT_COLORS.
 * This is the current (default) dark glassmorphism theme.
 */
import { PRIMITIVE_COLORS } from '../../residents/residentColors';
import { adminDarkComponents } from '../../admin/theme/adminDark';
import { TYPOGRAPHY } from './typography';
import { SPACING } from './spacing';
import { RADIUS } from './radius';
import { SHADOW_SHAPES } from './shadow';
import { MOTION } from './motion';
import type { AppTheme } from './types';

export const darkTheme: AppTheme = {
  mode: 'dark',

  redesign: {
    bg: {
      base: '#0a0a0a',
      modal: '#161618',
      gradientStart: '#12111a',
    },
    card: {
      bg: '#0E0E0E',
      bgStrong: '#0E0E0E',
      border: 'rgba(255,255,255,0.10)',
      borderSolid: 'rgba(255,255,255,0.08)',
      borderDashed: 'rgba(255,255,255,0.14)',
      borderHover: 'rgba(255,255,255,0.18)',
      shadowHover: '0 8px 24px rgba(0,0,0,0.55)',
    },
    glass: {
      buttonBg: 'rgba(255,255,255,0.04)',
      buttonBgHover: 'rgba(255,255,255,0.09)',
      buttonBorder: 'rgba(255,255,255,0.06)',
      buttonBgImage: 'rgba(0,0,0,0.35)',
      buttonBorderImage: 'rgba(255,255,255,0.08)',
    },
    text: {
      primary: '#f5f5f5',
      secondary: '#eaeaea',
      tertiary: '#e0e0e0',
      muted: '#999',
      subtle: '#777',
      dim: '#666',
      dimmer: '#555',
      imageSubtitle: 'rgba(255,255,255,0.6)',
    },
    separator: 'rgba(255,255,255,0.08)',
    activityIcon: {
      bg: 'rgba(255,255,255,0.05)',
      border: 'rgba(255,255,255,0.04)',
    },
    status: {
      inProgress: 'rgba(255,118,41,0.1)',
      inProgressText: '#ff7629',
    },
    accent: {
      green: '#66ce66',
      greenBg: 'rgba(102,206,102,0.15)',
      greenLight: 'rgba(102,206,102,0.12)',
      red: '#f55560',
      redBg: 'rgba(245,85,96,0.12)',
      orange: 'rgba(255,118,41,0.12)',
      orangeLight: 'rgba(255,118,41,0.1)',
      purple: 'rgba(190,94,200,0.12)',
      purpleLight: 'rgba(190,94,200,0.1)',
      blueBg: 'rgba(70,114,254,0.12)',
      blueBgLight: 'rgba(70,114,254,0.1)',
      blueBgMedium: 'rgba(70,114,254,0.15)',
      blueBgStrong: 'rgba(70,114,254,0.2)',
    },
    newsfeed: {
      announcementBg: 'rgba(14,14,20,0.92)',
      announcementBorder: 'rgba(70,114,254,0.25)',
      announcementBadgeBg: 'rgba(70,114,254,0.1)',
      tagActiveText: '#6b8efe',
      tagActiveBg: 'rgba(70,114,254,0.15)',
      tagActiveBorder: 'rgba(70,114,254,0.3)',
    },
    gradient: {
      redGlow: { color: 'rgba(245,85,96,0.15)', midColor: 'rgba(123,43,48,0.075)' },
      purpleGlow: { color: 'rgba(190,94,200,0.12)' },
      blueGlowTop: { color: 'rgba(71,115,254,0.18)', midColor: 'rgba(36,58,127,0.09)' },
      blueGlowBottom: { color: 'rgba(71,115,254,0.08)' },
      imageOverlay: [
        'rgba(10,10,10,0.25)',
        'rgba(10,10,10,0.5)',
        'rgba(10,10,10,0.7)',
        'rgba(10,10,10,0.82)',
        'rgba(10,10,10,0.92)',
        'rgba(10,10,10,0.97)',
        'rgb(10,10,10)',
      ],
    },
    sidebar: {
      itemHoverBg: 'rgba(255,255,255,0.04)',
      itemActiveBg: 'rgba(70,114,254,0.08)',
      indicator: PRIMITIVE_COLORS.resioBlue,
    },
  },

  semantic: {
    background: {
      primary: PRIMITIVE_COLORS.gray950,
      secondary: PRIMITIVE_COLORS.gray900,
      tertiary: PRIMITIVE_COLORS.gray850,
      // Darkened from gray700. At that value no text token cleared the 4.5:1
      // floor on an input in dark — the best available was 4.16 and the
      // placeholder actually in use measured 3.31. Moving one step down takes
      // text.secondary to 5.9 and the redesign muted token to 4.7, so
      // placeholders are legible without inventing a new colour. Body text in
      // the field improves from 8.33 to 11.81.
      input: PRIMITIVE_COLORS.gray850,
      overlay: PRIMITIVE_COLORS.black55,
      // A translucent tint, not a slab: the hover language (useHoverStyle
      // `row`) layers this over glass rows and table rows, the same family as
      // the sidebars' itemHoverBg. blackHover (#242424cc) is opaque grey and
      // read as a solid bar on those surfaces.
      hover: 'rgba(255,255,255,0.05)',
    },
    text: {
      primary: '#EEF1F4',
      secondary: PRIMITIVE_COLORS.gray300,
      tertiary: PRIMITIVE_COLORS.gray400,
      muted: PRIMITIVE_COLORS.gray500,
      light: '#E3E3E3',
      inverse: PRIMITIVE_COLORS.gray950,
      link: PRIMITIVE_COLORS.resioBlueLight,
    },
    border: {
      primary: PRIMITIVE_COLORS.resioBlueBorder,
      secondary: PRIMITIVE_COLORS.gray700,
      tertiary: PRIMITIVE_COLORS.gray800,
      input: PRIMITIVE_COLORS.gray500,
      focus: PRIMITIVE_COLORS.resioBlue,
    },
    interactive: {
      primary: PRIMITIVE_COLORS.resioBlue,
      primaryHover: PRIMITIVE_COLORS.resioBlueLight,
      secondary: PRIMITIVE_COLORS.resioPink,
      danger: PRIMITIVE_COLORS.resioDangerRed,
      disabled: PRIMITIVE_COLORS.gray600,
    },
    status: {
      success: PRIMITIVE_COLORS.successGreen,
      successBg: PRIMITIVE_COLORS.greenBg10,
      // On a dark surface the indicator green is already a usable ink — 6.75:1
      // on a payment card — so this matches `success` rather than diverging.
      // It replaces the payments palette's near-duplicate #5CBC5D (5.61:1).
      successText: PRIMITIVE_COLORS.successGreen,
      warning: PRIMITIVE_COLORS.warningOrange,
      error: PRIMITIVE_COLORS.resioRed,
      // Pairs at 4.61:1. Mirror of the light pair, inverted: a deep tinted
      // surface with the lighter brand red as the ink.
      errorBg: '#2A1416',
      errorText: PRIMITIVE_COLORS.resioRed,
      info: PRIMITIVE_COLORS.resioBlue,
    },
    icon: {
      primary: '#EEF1F4',
      secondary: PRIMITIVE_COLORS.gray400,
      muted: PRIMITIVE_COLORS.gray500,
      inactive: '#9CA3AF',
      inverse: PRIMITIVE_COLORS.gray950,
    },
    shadow: {
      card: 'rgba(0,0,0,0.40)',
      button: 'rgba(0,0,0,0.30)',
      dropdown: 'rgba(0,0,0,0.45)',
      fab: 'rgba(0,0,0,0.50)',
      modal: 'rgba(0,0,0,0.55)',
      focusRing: 'rgba(107,142,254,0.45)',
    },
  },

  components: {
    nav: {
      background: PRIMITIVE_COLORS.gray900,
      activeBackground: PRIMITIVE_COLORS.gray850,
      activeBorder: PRIMITIVE_COLORS.resioBlueLight,
      inactiveBorder: PRIMITIVE_COLORS.gray850,
      headerBlue: PRIMITIVE_COLORS.resioBlue,
      headerPurple: PRIMITIVE_COLORS.resioPurple,
    },
    button: {
      primaryBg: PRIMITIVE_COLORS.resioBlue,
      // Pure white, matching the light theme. The off-white this used to hold was
      // a value-only divergence between the two themes — themeParity checks key
      // symmetry, not values, so nothing flagged it — and it measured 3.99:1 on the
      // primary fill even after the brand-blue darkening, still under the AA floor.
      // White clears it at 4.53:1. Phase 4 reached the same conclusion from the
      // other direction when it rejected this token for filled-button labels.
      primaryText: '#FFFFFF',
      secondaryBg: 'transparent',
      secondaryBorder: PRIMITIVE_COLORS.resioBlue,
      secondaryText: PRIMITIVE_COLORS.resioBlueLight,
      dangerBg: PRIMITIVE_COLORS.resioPink,
      dangerText: PRIMITIVE_COLORS.gray950,
      outlineBg: 'transparent',
      outlineBorder: '#EEF1F4',
      outlineText: '#EEF1F4',
      disabledBg: PRIMITIVE_COLORS.gray600,
      disabledText: '#E3E3E3',
    },
    card: {
      background: PRIMITIVE_COLORS.gray900,
      border: PRIMITIVE_COLORS.gray700,
      readBackground: 'transparent',
      readBorder: PRIMITIVE_COLORS.gray850,
    },
    input: {
      background: PRIMITIVE_COLORS.gray700,
      border: PRIMITIVE_COLORS.gray500,
      focusBorder: PRIMITIVE_COLORS.resioBlue,
      placeholder: PRIMITIVE_COLORS.gray300,
      text: '#EEF1F4',
    },
    toggle: {
      background: PRIMITIVE_COLORS.gray850,
      activeBackground: PRIMITIVE_COLORS.gray700,
      trackOff: PRIMITIVE_COLORS.gray700,
      trackOn: PRIMITIVE_COLORS.resioBlue,
      thumb: '#EEF1F4',
    },
    avatar: {
      background: PRIMITIVE_COLORS.white10,
      text: '#EEF1F4',
      placeholder: PRIMITIVE_COLORS.gray850,
    },
    chat: {
      sentBubble: PRIMITIVE_COLORS.resioBlue,
      receivedBubble: PRIMITIVE_COLORS.gray850,
      inputBackground: PRIMITIVE_COLORS.white5,
      timestamp: PRIMITIVE_COLORS.white70,
    },
    notification: {
      unreadDot: PRIMITIVE_COLORS.warningOrange,
      unreadBackground: PRIMITIVE_COLORS.white5,
    },
    checkbox: {
      background: PRIMITIVE_COLORS.gray700,
      border: PRIMITIVE_COLORS.gray500,
      selected: PRIMITIVE_COLORS.resioBlue,
      checkmark: '#EEF1F4',
    },
    carousel: {
      dotActive: '#EEF1F4',
      dotInactive: PRIMITIVE_COLORS.gray500,
      background: PRIMITIVE_COLORS.black55,
    },
    badge: {
      priceBg: PRIMITIVE_COLORS.black55,
      attendingBg: PRIMITIVE_COLORS.greenBg10,
      attendingText: PRIMITIVE_COLORS.successGreen,
      purpleBg: PRIMITIVE_COLORS.purpleBg50,
      purpleText: PRIMITIVE_COLORS.resioPurpleLight,
    },
    gradient: {
      orangeadeStart: '#EF303D',
      orangeadeEnd: '#073EEC',
    },
    admin: adminDarkComponents,
  },

  typography: TYPOGRAPHY,
  spacing: SPACING,
  radius: RADIUS,
  shadow: SHADOW_SHAPES,
  motion: MOTION,
};
